import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, MessageSquare, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isResponseDialogOpen, setIsResponseDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [formData, setFormData] = useState({
    subscriber_id: '',
    subject: '',
    message: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/support/tickets`),
        axios.get(`${API}/subscribers`)
      ]);
      setTickets(ticketsRes.data);
      setSubscribers(subscribersRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/support/tickets`, formData);
      toast.success('Support ticket created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create ticket');
    }
  };

  const handleRespond = async () => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    try {
      await axios.put(`${API}/support/tickets/${selectedTicket.id}/respond`, {
        response: responseText
      });
      toast.success('Response sent successfully');
      setIsResponseDialogOpen(false);
      setResponseText('');
      setSelectedTicket(null);
      fetchData();
    } catch (error) {
      console.error('Error responding to ticket:', error);
      toast.error('Failed to send response');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}/status?status=${newStatus}`);
      toast.success('Ticket status updated');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      subscriber_id: '',
      subject: '',
      message: '',
      priority: 'medium'
    });
  };

  const openResponseDialog = (ticket) => {
    setSelectedTicket(ticket);
    setResponseText(ticket.response || '');
    setIsResponseDialogOpen(true);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'resolved':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'closed':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const openTickets = tickets.filter(t => t.status === 'open');
  const resolvedTickets = tickets.filter(t => t.status === 'resolved');
  const closedTickets = tickets.filter(t => t.status === 'closed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-jakarta font-bold text-slate-900">Support Tickets</h2>
          <p className="text-slate-600 mt-1">Manage subscriber issues and requests</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-ticket-btn">
              <Plus size={20} className="mr-2" />
              Create Ticket
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="ticket-dialog">
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
              <DialogDescription>
                Report an issue for a subscriber
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="subscriber">Subscriber *</Label>
                <Select value={formData.subscriber_id} onValueChange={(value) => setFormData({ ...formData, subscriber_id: value })} required>
                  <SelectTrigger data-testid="ticket-subscriber-select">
                    <SelectValue placeholder="Select subscriber" />
                  </SelectTrigger>
                  <SelectContent>
                    {subscribers.map((subscriber) => (
                      <SelectItem key={subscriber.id} value={subscriber.id}>
                        {subscriber.name} - {subscriber.mobile}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="e.g., Internet connection down"
                  required
                  data-testid="ticket-subject-input"
                />
              </div>
              <div>
                <Label htmlFor="message">Message *</Label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe the issue..."
                  required
                  rows="4"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="ticket-message-input"
                />
              </div>
              <div>
                <Label htmlFor="priority">Priority *</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                  <SelectTrigger data-testid="ticket-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-btn">
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-ticket-btn">
                  Create Ticket
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600 font-medium">Open Tickets</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">{openTickets.length}</p>
            </div>
            <Clock className="text-amber-500" size={32} />
          </div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-600 font-medium">Resolved</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">{resolvedTickets.length}</p>
            </div>
            <CheckCircle className="text-emerald-500" size={32} />
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium">Total Tickets</p>
              <p className="text-2xl font-bold text-slate-700 mt-1">{tickets.length}</p>
            </div>
            <MessageSquare className="text-slate-500" size={32} />
          </div>
        </div>
      </div>

      {/* Tickets Tabs */}
      <Tabs defaultValue="open" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="open">Open ({openTickets.length})</TabsTrigger>
          <TabsTrigger value="resolved">Resolved ({resolvedTickets.length})</TabsTrigger>
          <TabsTrigger value="all">All ({tickets.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="open" className="space-y-4 mt-6">
          {openTickets.length > 0 ? (
            openTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`ticket-${ticket.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{ticket.subject}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                    <p className="text-slate-600 mb-3">{ticket.message}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="font-medium">{ticket.subscriber_name}</span>
                      <span>{ticket.subscriber_mobile}</span>
                      <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openResponseDialog(ticket)}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid={`respond-ticket-${ticket.id}`}
                    >
                      Respond
                    </Button>
                    <Button
                      onClick={() => handleStatusChange(ticket.id, 'closed')}
                      variant="outline"
                      className="border-slate-300 hover:bg-slate-100"
                      data-testid={`close-ticket-${ticket.id}`}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare size={48} className="mx-auto mb-3 text-slate-300" />
              <p>No open tickets</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="resolved" className="space-y-4 mt-6">
          {resolvedTickets.length > 0 ? (
            resolvedTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm"
                data-testid={`ticket-${ticket.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{ticket.subject}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                    <p className="text-slate-600 mb-3">{ticket.message}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="font-medium">{ticket.subscriber_name}</span>
                      <span>{ticket.subscriber_mobile}</span>
                      <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                {ticket.response && (
                  <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-sm font-semibold text-emerald-900 mb-1">Response:</p>
                    <p className="text-slate-700">{ticket.response}</p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle size={48} className="mx-auto mb-3 text-slate-300" />
              <p>No resolved tickets</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4 mt-6">
          {tickets.length > 0 ? (
            tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm"
                data-testid={`ticket-${ticket.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{ticket.subject}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                    <p className="text-slate-600 mb-3">{ticket.message}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="font-medium">{ticket.subscriber_name}</span>
                      <span>{ticket.subscriber_mobile}</span>
                      <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {ticket.status === 'open' && (
                    <Button
                      onClick={() => openResponseDialog(ticket)}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid={`respond-ticket-${ticket.id}`}
                    >
                      Respond
                    </Button>
                  )}
                </div>
                {ticket.response && (
                  <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-sm font-semibold text-emerald-900 mb-1">Response:</p>
                    <p className="text-slate-700">{ticket.response}</p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare size={48} className="mx-auto mb-3 text-slate-300" />
              <p>No tickets yet</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Response Dialog */}
      <Dialog open={isResponseDialogOpen} onOpenChange={setIsResponseDialogOpen}>
        <DialogContent data-testid="response-dialog">
          <DialogHeader>
            <DialogTitle>Respond to Ticket</DialogTitle>
            <DialogDescription>
              Send a response to {selectedTicket?.subscriber_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedTicket && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm font-semibold text-slate-900 mb-1">Issue:</p>
                <p className="text-slate-700">{selectedTicket.message}</p>
              </div>
            )}
            <div>
              <Label htmlFor="response">Your Response *</Label>
              <textarea
                id="response"
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Type your response here..."
                rows="6"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="response-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setIsResponseDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleRespond} className="bg-slate-900 hover:bg-slate-800" data-testid="send-response-btn">
                Send Response
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Support;
